import { FilterGroup, FilterCondition } from './ScimFilterBuilder';

// Token types for the lexer
export enum TokenType {
    IDENTIFIER = 'IDENTIFIER',
    OPERATOR = 'OPERATOR',
    LOGICAL_OPERATOR = 'LOGICAL_OPERATOR',
    STRING_LITERAL = 'STRING_LITERAL',
    LEFT_PAREN = 'LEFT_PAREN',
    RIGHT_PAREN = 'RIGHT_PAREN',
    NOT = 'NOT',
    FUNCTION = 'FUNCTION',
    WHITESPACE = 'WHITESPACE',
    EOF = 'EOF'
}

// Token structure
export interface Token {
    type: TokenType;
    value: string;
    position: number;
}

// AST node types
export interface ComparisonNode {
    type: 'comparison';
    attribute: string;
    operator: string;
    value?: string;
    function?: string;
}

export interface LogicalNode {
    type: 'logical';
    operator: 'and' | 'or';
    left: AstNode;
    right: AstNode;
}

export interface NotNode {
    type: 'not';
    operand: AstNode;
}

export interface GroupNode {
    type: 'group';
    expression: AstNode;
}

export type AstNode = ComparisonNode | LogicalNode | NotNode | GroupNode;

// Lexer class
export class ScimFilterLexer {
    private input: string;
    private position: number = 0;
    private tokens: Token[] = [];

    private static readonly OPERATORS = new Set(['eq', 'ne', 'co', 'sw', 'ew', 'pr', 'gt', 'ge', 'lt', 'le']);
    private static readonly LOGICAL_OPERATORS = new Set(['and', 'or']);
    private static readonly FUNCTIONS = new Set(['tolower', 'toupper', 'trim', 'length']);

    constructor(input: string) {
        this.input = input;
    }

    tokenize(): Token[] {
        this.tokens = [];
        this.position = 0;

        while (this.position < this.input.length) {
            const char = this.input[this.position];

            if (this.isWhitespace(char)) {
                this.skipWhitespace();
            } else if (char === '"' || char === "'") {
                this.tokens.push(this.readStringLiteral());
            } else if (char === '(') {
                this.tokens.push({ type: TokenType.LEFT_PAREN, value: '(', position: this.position++ });
            } else if (char === ')') {
                this.tokens.push({ type: TokenType.RIGHT_PAREN, value: ')', position: this.position++ });
            } else if (this.isLetter(char)) {
                this.tokens.push(this.readIdentifier());
            } else {
                // Skip unknown characters
                this.position++;
            }
        }

        this.tokens.push({ type: TokenType.EOF, value: '', position: this.position });
        return this.tokens.filter(token => token.type !== TokenType.WHITESPACE);
    }

    private isWhitespace(char: string): boolean {
        return /\s/.test(char);
    }

    private isLetter(char: string): boolean {
        return /[a-zA-Z_.]/.test(char);
    }

    private isLetterOrDigit(char: string): boolean {
        return /[a-zA-Z0-9_.]/.test(char);
    }

    private skipWhitespace(): void {
        while (this.position < this.input.length && this.isWhitespace(this.input[this.position])) {
            this.position++;
        }
    }

    private readStringLiteral(): Token {
        const startPosition = this.position;
        const quoteChar = this.input[this.position]; // Remember the opening quote type
        this.position++; // Skip opening quote

        let value = '';
        let escaped = false;

        while (this.position < this.input.length) {
            const char = this.input[this.position];

            if (escaped) {
                // Handle escape sequences
                switch (char) {
                    case '"': value += '"'; break;
                    case "'": value += "'"; break;
                    case '\\': value += '\\'; break;
                    case '/': value += '/'; break;
                    case 'b': value += '\b'; break;
                    case 'f': value += '\f'; break;
                    case 'n': value += '\n'; break;
                    case 'r': value += '\r'; break;
                    case 't': value += '\t'; break;
                    default: value += char; break;
                }
                escaped = false;
            } else if (char === '\\') {
                escaped = true;
            } else if (char === quoteChar) {
                this.position++; // Skip closing quote
                break;
            } else {
                value += char;
            }

            this.position++;
        }

        return { type: TokenType.STRING_LITERAL, value, position: startPosition };
    }

    private readIdentifier(): Token {
        const startPosition = this.position;
        let value = '';

        while (this.position < this.input.length && this.isLetterOrDigit(this.input[this.position])) {
            value += this.input[this.position];
            this.position++;
        }

        const valueLower = value.toLowerCase();

        // Determine token type based on value (SCIM operators and keywords are case-insensitive)
        let type: TokenType;
        let tokenValue = value;
        if (ScimFilterLexer.OPERATORS.has(valueLower)) {
            type = TokenType.OPERATOR;
            tokenValue = valueLower;
        } else if (ScimFilterLexer.LOGICAL_OPERATORS.has(valueLower)) {
            type = TokenType.LOGICAL_OPERATOR;
            tokenValue = valueLower; // normalize to 'and' | 'or'
        } else if (valueLower === 'not') {
            type = TokenType.NOT;
            tokenValue = valueLower;
        } else if (ScimFilterLexer.FUNCTIONS.has(valueLower)) {
            type = TokenType.FUNCTION;
            tokenValue = valueLower;
        } else {
            type = TokenType.IDENTIFIER;
        }

        return { type, value: tokenValue, position: startPosition };
    }
}

// Parser class
export class ScimFilterParser {
    private tokens: Token[];
    private currentIndex: number = 0;

    constructor(tokens: Token[]) {
        this.tokens = tokens;
    }

    parse(): AstNode | null {
        if (this.tokens.length === 0 || this.tokens[0].type === TokenType.EOF) {
            return null;
        }
        return this.parseExpression();
    }

    private get currentToken(): Token {
        return this.tokens[this.currentIndex] || { type: TokenType.EOF, value: '', position: 0 };
    }

    private advance(): void {
        this.currentIndex++;
    }

    private match(type: TokenType): boolean {
        if (this.currentToken.type === type) {
            this.advance();
            return true;
        }
        return false;
    }

    private parseExpression(): AstNode {
        let left = this.parseTerm();

        while (this.currentToken.type === TokenType.LOGICAL_OPERATOR) {
            const operator = this.currentToken.value as 'and' | 'or';
            this.advance();
            const right = this.parseTerm();
            left = { type: 'logical', operator, left, right };
        }

        return left;
    }

    private parseTerm(): AstNode {
        if (this.currentToken.type === TokenType.NOT) {
            this.advance();
            const operand = this.parseTerm();
            return { type: 'not', operand };
        }

        if (this.match(TokenType.LEFT_PAREN)) {
            const expression = this.parseExpression();
            if (!this.match(TokenType.RIGHT_PAREN)) {
                throw new Error('Expected closing parenthesis');
            }
            return { type: 'group', expression };
        }

        return this.parseComparison();
    }

    private parseComparison(): ComparisonNode {
        let attribute = '';
        let functionName: string | undefined;

        // Check for function
        if (this.currentToken.type === TokenType.FUNCTION) {
            functionName = this.currentToken.value;
            this.advance();

            if (!this.match(TokenType.LEFT_PAREN)) {
                throw new Error('Expected opening parenthesis after function');
            }

            // After opening parenthesis, we expect an identifier
            const nextToken = this.currentToken;
            if (nextToken.type !== TokenType.IDENTIFIER) {
                throw new Error('Expected identifier in function');
            }
            attribute = nextToken.value;
            this.advance();

            if (!this.match(TokenType.RIGHT_PAREN)) {
                throw new Error('Expected closing parenthesis in function');
            }
        } else if (this.currentToken.type === TokenType.IDENTIFIER) {
            attribute = this.currentToken.value;
            this.advance();
        } else {
            throw new Error('Expected identifier or function');
        }

        const operatorToken = this.currentToken;
        if (operatorToken.type !== TokenType.OPERATOR) {
            throw new Error('Expected operator');
        }
        const operator = operatorToken.value;
        this.advance();

        let value: string | undefined;
        if (operator !== 'pr') {
            const valueToken = this.currentToken;
            if (valueToken.type !== TokenType.STRING_LITERAL) {
                throw new Error('Expected string literal for comparison value');
            }
            value = valueToken.value;
            this.advance();
        }

        return {
            type: 'comparison',
            attribute,
            operator,
            value,
            function: functionName
        };
    }
}

// Converter to transform AST to FilterGroup structure
export class ScimFilterConverter {
    private generateId(): string {
        return `filter_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    convertToFilterGroup(ast: AstNode): FilterGroup {
        const rootGroup: FilterGroup = {
            id: this.generateId(),
            operator: 'and',
            conditions: [],
            groups: []
        };

        this.processNode(ast, rootGroup);
        return rootGroup;
    }

    private processNode(node: AstNode, group: FilterGroup): void {
        switch (node.type) {
            case 'comparison':
                const condition: FilterCondition = {
                    id: this.generateId(),
                    field: node.attribute,
                    operator: node.operator,
                    value: node.value || '',
                    function: node.function
                };
                group.conditions.push(condition);
                break;

            case 'logical':
                if (node.operator === group.operator) {
                    // Same operator, add to current group
                    this.processNode(node.left, group);
                    this.processNode(node.right, group);
                } else {
                    // Different operator, create new group
                    const newGroup: FilterGroup = {
                        id: this.generateId(),
                        operator: node.operator,
                        conditions: [],
                        groups: []
                    };

                    this.processNode(node.left, newGroup);
                    this.processNode(node.right, newGroup);
                    group.groups.push(newGroup);
                }
                break;

            case 'not':
                // For NOT operations, we'll create a separate group
                // This is a simplified approach - in practice, you might want more sophisticated handling
                const notGroup: FilterGroup = {
                    id: this.generateId(),
                    operator: 'and',
                    conditions: [],
                    groups: []
                };
                this.processNode(node.operand, notGroup);
                group.groups.push(notGroup);
                break;

            case 'group':
                const groupNode: FilterGroup = {
                    id: this.generateId(),
                    operator: 'and',
                    conditions: [],
                    groups: []
                };
                this.processNode(node.expression, groupNode);
                group.groups.push(groupNode);
                break;
        }
    }
}

// Main parsing function
export function parseScimFilter(filterString: string): FilterGroup | null {
    try {
        const lexer = new ScimFilterLexer(filterString);
        const tokens = lexer.tokenize();

        const parser = new ScimFilterParser(tokens);
        const ast = parser.parse();

        if (!ast) {
            return null;
        }

        const converter = new ScimFilterConverter();
        return converter.convertToFilterGroup(ast);
    } catch (error) {
        // Parsing failures are expected when users are typing or validating filters.
        // Callers should treat `null` as "invalid/unparseable" without spamming the console.
        return null;
    }
}

// API dialect: property count filters (e.g. membertransitive $count.gt 999) are valid at API level
// but use syntax the builder doesn't parse. We accept them as valid so manual filter input doesn't show "invalid".
const API_EXTENSION_COUNT_PATTERN = /\$count\s*\.\s*(gt|ge|lt|le|eq|ne)/i;

/** True if the filter uses API extensions (e.g. $count) that the builder cannot edit. */
export function isApiExtensionFilter(filterString: string): boolean {
    return API_EXTENSION_COUNT_PATTERN.test((filterString || '').trim());
}

export function validateScimFilter(filterString: string): { isValid: boolean; error?: string } {
    const trimmed = (filterString || '').trim();
    if (!trimmed) {
        return { isValid: true };
    }
    try {
        const result = parseScimFilter(filterString);
        if (result !== null) return { isValid: true };
        if (API_EXTENSION_COUNT_PATTERN.test(filterString)) {
            return { isValid: true };
        }
        return { isValid: false, error: 'Filter could not be parsed' };
    } catch (error) {
        if (API_EXTENSION_COUNT_PATTERN.test(filterString)) {
            return { isValid: true };
        }
        return {
            isValid: false,
            error: error instanceof Error ? error.message : 'Unknown parsing error'
        };
    }
}

// Helper function to extract field names from a SCIM filter
export function extractFieldsFromFilter(filterString: string): string[] {
    try {
        const lexer = new ScimFilterLexer(filterString);
        const tokens = lexer.tokenize();

        const fields = new Set<string>();
        for (const token of tokens) {
            if (token.type === TokenType.IDENTIFIER) {
                fields.add(token.value);
            }
        }

        return Array.from(fields);
    } catch (error) {
        return [];
    }
}
